/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { debounce as _debounce, map as _map, uniq as _uniq } from 'lodash';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';

import { CircularProgress, Switch, Typography } from '@mui/material';

import { Namespaces } from '../../../../i18n';
import { UserApiService } from '../../../../services/UserApiService';
import { RootState } from '../../../../store';
import { authSetUser } from '../../../../store/auth/actions';
import { enqueueHUSnackbar } from '../../../../store/notifier/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { useConstant } from '../../../../utils/useConstant';
import { useObjectState } from '../../../../utils/useObjectState';
import { TwoSwitchesHeading } from './components/TwoSwitchesHeading';
import { TwoSwitchesRow } from './components/TwoSwitchesRow';
import { firstPartyClusterNotifications, secondPartyClusterNotifications } from './Notifications.consts';
import { useNotificationsStyles } from './Notifications.styles';
import { UIEmailNotificationGroup } from './Notifications.types';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type State = {
  selected: string[];
  isFetching: boolean;
};

const NotificationsComponent = memo<PropsFromRedux>(props => {
  const { auth } = props;
  const { t } = useTranslation(Namespaces.UserProfile);
  const dispatch = useDispatch();
  const [state, setState] = useObjectState<State>({
    selected: [],
    isFetching: false,
  });
  const classes = useNotificationsStyles();
  const notificationSettings = auth.user?.notificationSettings;

  const { selected, isFetching } = state;

  const saveChanges = async (nextSelected: string[]) => {
    try {
      setState({ isFetching: true });
      const result = await UserApiService.updateUser({
        notificationSettings: nextSelected.map(v => ({ notification: v })),
      });
      dispatch(authSetUser(result.data.payload));
      dispatch(
        enqueueHUSnackbar(
          { primaryText: t('notifications.label.changes_saved') },
          {
            variant: 'success',
            autoHideDuration: 1200,
          },
        ),
      );
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
    } finally {
      setState({ isFetching: false });
    }
  };

  const debouncedSaveChanges = useConstant(() => _debounce(saveChanges, 1000));

  useEffect(() => {
    if (notificationSettings) {
      setState({ selected: notificationSettings.map(v => v.notification) });
    }
  }, [notificationSettings, setState]);

  const reactToCommonToggle = (group: UIEmailNotificationGroup, checked: boolean, ui: boolean) => {
    const list = ui ? group.uiNotifications : group.emailNotifications;
    const nextSelected = checked ? _uniq([...selected, ...list]) : selected.filter(v => !list.includes(v));
    setState({ selected: nextSelected });
    void debouncedSaveChanges(nextSelected);
  };

  const toggleFirstNotifications = (checked: boolean, ui: boolean) => {
    reactToCommonToggle(firstPartyClusterNotifications, checked, ui);
  };

  const toggleSecondNotifications = (checked: boolean, ui: boolean) => {
    reactToCommonToggle(secondPartyClusterNotifications, checked, ui);
  };

  const isChecked = (name: string) => {
    return selected.includes(name);
  };

  const isCommonFirstChecked = (ui: boolean) => {
    const list = ui
      ? firstPartyClusterNotifications.uiNotifications
      : firstPartyClusterNotifications.emailNotifications;
    return list.some(v => selected.includes(v));
  };

  const isCommonSecondChecked = (ui: boolean) => {
    const list = ui
      ? secondPartyClusterNotifications.uiNotifications
      : secondPartyClusterNotifications.emailNotifications;
    return list.some(v => selected.includes(v));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    let nextSelected: string[];
    if (checked) {
      nextSelected = [...selected, name];
    } else {
      nextSelected = selected.filter(v => v !== name);
    }
    setState({ selected: nextSelected });
    void debouncedSaveChanges(nextSelected);
  };

  if (!auth.user) {
    return <CircularProgress color="primary" size={40} />;
  }
  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.screenTitle}>
        {t('notifications.screen_title')}
      </Typography>
      <div className={classes.gridTwoSwitches}>
        <TwoSwitchesHeading
          groupNameText={t('notifications.group_name.first_party_clusters')}
          groupNameTooltip={t('notifications.group_name.first_party_clusters_tooltip')}
          firstHeadingText={t('notifications.notification_type.web_and_mobile')}
          secondHeadingText={t('notifications.notification_type.email')}
        />
        <TwoSwitchesRow
          common
          notificationNameText={t('notifications.label.receive_notifications')}
          firstSwitch={
            <Switch
              checked={isCommonFirstChecked(true)}
              disabled={isFetching}
              onChange={(_e: any, checked: boolean) => toggleFirstNotifications(checked, true)}
            />
          }
          secondSwitch={
            <Switch
              checked={isCommonFirstChecked(false)}
              disabled={isFetching}
              onChange={(_e: any, checked: boolean) => toggleFirstNotifications(checked, false)}
            />
          }
        />
        {_map(firstPartyClusterNotifications.rowsByType, ([uiName, emailName], type) => (
          <TwoSwitchesRow
            key={[type, 'first_party_clusters'].join('_')}
            notificationNameText={t(`notifications.notification_name.${type}`)}
            firstSwitch={
              <Switch
                color="primary"
                checked={isChecked(uiName)}
                onChange={onChange}
                disabled={isFetching}
                name={uiName}
              />
            }
            secondSwitch={
              <Switch
                color="primary"
                checked={isChecked(emailName)}
                onChange={onChange}
                disabled={isFetching}
                name={emailName}
              />
            }
          />
        ))}
        <div className={classes.settingsDivider} />
        <TwoSwitchesHeading
          groupNameText={t('notifications.group_name.second_party_clusters')}
          groupNameTooltip={t('notifications.group_name.second_party_clusters_tooltip')}
          firstHeadingText={t('notifications.notification_type.web_and_mobile')}
          secondHeadingText={t('notifications.notification_type.email')}
        />
        <TwoSwitchesRow
          common
          notificationNameText={t('notifications.label.receive_notifications')}
          firstSwitch={
            <Switch
              checked={isCommonSecondChecked(true)}
              disabled={isFetching}
              onChange={(_e: any, checked: boolean) => toggleSecondNotifications(checked, true)}
            />
          }
          secondSwitch={
            <Switch
              checked={isCommonSecondChecked(false)}
              disabled={isFetching}
              onChange={(_e: any, checked: boolean) => toggleSecondNotifications(checked, false)}
            />
          }
        />
        {_map(secondPartyClusterNotifications.rowsByType, ([uiName, emailName], type) => (
          <TwoSwitchesRow
            key={[type, 'second_party_clusters'].join('_')}
            notificationNameText={t(`notifications.notification_name.${type}`)}
            firstSwitch={
              <Switch
                color="primary"
                checked={isChecked(uiName)}
                onChange={onChange}
                disabled={isFetching}
                name={uiName}
              />
            }
            secondSwitch={
              <Switch
                color="primary"
                checked={isChecked(emailName)}
                onChange={onChange}
                disabled={isFetching}
                name={emailName}
              />
            }
          />
        ))}
      </div>
    </div>
  );
});

export const Notifications = connector(NotificationsComponent);
